/**
 * Blog SEO Command Center
 * 
 * Comprehensive admin dashboard for AI blog generation, SEO optimization,
 * scheduling, analytics, and content management.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    PencilIcon,
    CalendarIcon,
    ChartBarIcon,
    MagnifyingGlassIcon,
    Cog6ToothIcon,
    SparklesIcon,
    ClockIcon,
    ArrowTrendingUpIcon,
    DocumentTextIcon,
    FlagIcon,
    PhotoIcon,
    ArrowPathIcon,
    PlayIcon,
    PauseIcon,
    PlusIcon,
    PencilSquareIcon,
    TrashIcon,
    EyeIcon,
    CheckCircleIcon,
    ExclamationCircleIcon,
    XCircleIcon,
    ChevronRightIcon,
    ChevronDownIcon,
    FunnelIcon,
    ArrowDownTrayIcon,
    ArrowTopRightOnSquareIcon,
    Squares2X2Icon,
    BoltIcon,
    TrophyIcon,
    ExclamationTriangleIcon,
    InformationCircleIcon,
    ListBulletIcon,
    Squares2X2Icon as GridIcon,
    EllipsisVerticalIcon,
    DocumentDuplicateIcon,
    PaperAirplaneIcon,
    BookmarkIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import blogCommandCenterService from '../../../services/blogCommandCenterService';

// =============================================================================
// CONSTANTS & HELPERS
// =============================================================================

const TABS = [
    { id: 'overview', label: 'Overview', icon: ChartBarIcon },
    { id: 'generate', label: 'AI Generate', icon: SparklesIcon },
    { id: 'posts', label: 'Posts', icon: DocumentTextIcon },
    { id: 'schedule', label: 'Schedule', icon: CalendarIcon },
    { id: 'seo', label: 'SEO Audit', icon: FlagIcon },
    { id: 'keywords', label: 'Keywords', icon: MagnifyingGlassIcon },
    { id: 'queue', label: 'Content Queue', icon: Squares2X2Icon },
    { id: 'analytics', label: 'Analytics', icon: ArrowTrendingUpIcon },
    { id: 'images', label: 'Images', icon: PhotoIcon },
    { id: 'settings', label: 'Settings', icon: Cog6ToothIcon }
];

const STATUS_COLORS = {
    published: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    draft: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
    scheduled: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    in_review: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    archived: 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const SEO_SCORE_COLORS = {
    excellent: 'text-emerald-400',
    good: 'text-green-400',
    average: 'text-amber-400',
    poor: 'text-orange-400',
    critical: 'text-red-400'
};

function getSeoScoreLabel(score) {
    if (score >= 90) return { label: 'Excellent', color: SEO_SCORE_COLORS.excellent };
    if (score >= 70) return { label: 'Good', color: SEO_SCORE_COLORS.good };
    if (score >= 50) return { label: 'Average', color: SEO_SCORE_COLORS.average };
    if (score >= 30) return { label: 'Poor', color: SEO_SCORE_COLORS.poor };
    return { label: 'Critical', color: SEO_SCORE_COLORS.critical };
}

function formatNumber(num) {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toString() || '0';
}

function formatDate(date) {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    });
}

function formatTime(date) {
    if (!date) return '';
    return new Date(date).toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
    });
}

// =============================================================================
// STAT CARD COMPONENT
// =============================================================================

function StatCard({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) {
    const colors = {
        blue: 'from-blue-500/20 to-blue-600/10 border-blue-500/30',
        emerald: 'from-emerald-500/20 to-emerald-600/10 border-emerald-500/30',
        amber: 'from-amber-500/20 to-amber-600/10 border-amber-500/30',
        purple: 'from-purple-500/20 to-purple-600/10 border-purple-500/30',
        rose: 'from-rose-500/20 to-rose-600/10 border-rose-500/30',
        cyan: 'from-cyan-500/20 to-cyan-600/10 border-cyan-500/30'
    };

    const iconColors = {
        blue: 'text-blue-400',
        emerald: 'text-emerald-400',
        amber: 'text-amber-400',
        purple: 'text-purple-400',
        rose: 'text-rose-400',
        cyan: 'text-cyan-400'
    };

    return (
        <div
            className={`relative overflow-hidden rounded-xl bg-gradient-to-br ${colors[color]} border p-5 transition-all duration-300`}
        >
            <div className="flex items-start justify-between">
                <div>
                    <p className="text-sm text-slate-400 mb-1">{title}</p>
                    <p className="text-2xl font-bold text-white">{value}</p>
                    {subtitle && <p className="text-xs text-slate-500 mt-1">{subtitle}</p>}
                    {trend && (
                        <div className={`flex items-center gap-1 mt-2 text-xs ${trend > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            <ArrowTrendingUpIcon className={`w-3 h-3 ${trend < 0 ? 'rotate-180' : ''}`} />
                            <span>{Math.abs(trend)}% from last week</span>
                        </div>
                    )}
                </div>
                <div className={`p-3 rounded-lg bg-slate-800/50 ${iconColors[color]}`}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// OVERVIEW TAB
// =============================================================================

function OverviewTab({ stats, loading, onRefresh }) {
    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Quick Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <StatCard
                    title="Total Posts"
                    value={stats?.total_posts || 0}
                    icon={DocumentTextIcon}
                    color="blue"
                />
                <StatCard
                    title="Published"
                    value={stats?.published_posts || 0}
                    icon={CheckCircleIcon}
                    color="emerald"
                />
                <StatCard
                    title="Drafts"
                    value={stats?.draft_posts || 0}
                    icon={PencilSquareIcon}
                    color="amber"
                />
                <StatCard
                    title="Scheduled"
                    value={stats?.scheduled_posts || 0}
                    icon={CalendarIcon}
                    color="purple"
                />
                <StatCard
                    title="Avg SEO Score"
                    value={stats?.avg_seo_score || 0}
                    icon={FlagIcon}
                    color="cyan"
                />
                <StatCard
                    title="Total Views"
                    value={formatNumber(stats?.total_views || 0)}
                    icon={EyeIcon}
                    color="rose"
                />
            </div>

            {/* Quick Actions & Recent Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Quick Actions */}
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <BoltIcon className="w-5 h-5 text-amber-400" />
                        Quick Actions
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                        <button className="flex items-center gap-3 p-4 bg-gradient-to-br from-blue-500/20 to-blue-600/10 border border-blue-500/30 rounded-lg hover:from-blue-500/30 transition-all group">
                            <SparklesIcon className="w-5 h-5 text-blue-400 group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-medium text-white">Generate Post</span>
                        </button>
                        <button className="flex items-center gap-3 p-4 bg-gradient-to-br from-emerald-500/20 to-emerald-600/10 border border-emerald-500/30 rounded-lg hover:from-emerald-500/30 transition-all group">
                            <FlagIcon className="w-5 h-5 text-emerald-400 group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-medium text-white">Run SEO Audit</span>
                        </button>
                        <button className="flex items-center gap-3 p-4 bg-gradient-to-br from-purple-500/20 to-purple-600/10 border border-purple-500/30 rounded-lg hover:from-purple-500/30 transition-all group">
                            <PlusIcon className="w-5 h-5 text-purple-400 group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-medium text-white">Add to Queue</span>
                        </button>
                        <button className="flex items-center gap-3 p-4 bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border border-cyan-500/30 rounded-lg hover:from-cyan-500/30 transition-all group">
                            <MagnifyingGlassIcon className="w-5 h-5 text-cyan-400 group-hover:scale-110 transition-transform" />
                            <span className="text-sm font-medium text-white">Research Keywords</span>
                        </button>
                    </div>
                </div>

                {/* Schedule Status */}
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-blue-400" />
                        Auto-Generation Schedule
                    </h3>
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Status</span>
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${stats?.schedule_enabled
                                ? 'bg-emerald-500/20 text-emerald-400'
                                : 'bg-slate-500/20 text-slate-400'
                                }`}>
                                {stats?.schedule_enabled ? 'Active' : 'Paused'}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Next Run</span>
                            <span className="text-white">
                                {stats?.next_scheduled_run
                                    ? `${formatDate(stats.next_scheduled_run)} ${formatTime(stats.next_scheduled_run)}`
                                    : 'Not scheduled'
                                }
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Pending Generations</span>
                            <span className="text-white">{stats?.pending_generations || 0}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-400">Failed Today</span>
                            <span className={stats?.failed_generations_today > 0 ? 'text-red-400' : 'text-white'}>
                                {stats?.failed_generations_today || 0}
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <ChartBarIcon className="w-5 h-5 text-purple-400" />
                    This Week vs Last Week
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                    <div>
                        <p className="text-sm text-slate-400 mb-1">Posts Created</p>
                        <p className="text-2xl font-bold text-white">{stats?.posts_this_week || 0}</p>
                    </div>
                    <div>
                        <p className="text-sm text-slate-400 mb-1">Views This Week</p>
                        <p className="text-2xl font-bold text-white">{formatNumber(stats?.views_this_week || 0)}</p>
                    </div>
                    <div>
                        <p className="text-sm text-slate-400 mb-1">Content Queue</p>
                        <p className="text-2xl font-bold text-white">{stats?.content_queue_count || 0}</p>
                    </div>
                    <div>
                        <p className="text-sm text-slate-400 mb-1">Keywords Tracked</p>
                        <p className="text-2xl font-bold text-white">{stats?.keywords_tracked || 0}</p>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// AI GENERATE TAB
// =============================================================================

function GenerateTab({ topics, onGenerate, onTopicsChange }) {
    const [selectedTopic, setSelectedTopic] = useState(null);
    const [contentBrief, setContentBrief] = useState('');
    const [targetKeywords, setTargetKeywords] = useState('');
    const [contentLength, setContentLength] = useState('medium');
    const [tone, setTone] = useState('professional');
    const [includeFaqs, setIncludeFaqs] = useState(true);
    const [includeImages, setIncludeImages] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationHistory, setGenerationHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [showNewTopicModal, setShowNewTopicModal] = useState(false);
    const [newTopic, setNewTopic] = useState({
        title: '',
        description: '',
        seo_keywords: '',
        target_word_count: 1000,
        priority: 50,
        generation_prompt: ''
    });
    const [creatingTopic, setCreatingTopic] = useState(false);

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        setHistoryLoading(true);
        try {
            const data = await blogCommandCenterService.getGenerationHistory({ limit: 10 });
            setGenerationHistory(data.items || []);
        } catch (error) {
            console.error('Error loading generation history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleGenerate = async () => {
        if (!selectedTopic) {
            alert('Please select a topic');
            return;
        }

        setIsGenerating(true);
        try {
            await blogCommandCenterService.generateBlogPost({
                topicId: selectedTopic.id,
                contentBrief,
                targetKeywords: targetKeywords.split(',').map(k => k.trim()).filter(Boolean),
                contentLength,
                tone,
                includeFaqs,
                includeImages
            });
            await loadHistory();
            // Reset form
            setContentBrief('');
            setTargetKeywords('');
        } catch (error) {
            console.error('Generation error:', error);
            alert('Failed to generate post: ' + error.message);
        } finally {
            setIsGenerating(false);
        }
    };

    const handleCreateTopic = async () => {
        if (!newTopic.title.trim()) {
            alert('Please enter a topic title');
            return;
        }

        setCreatingTopic(true);
        try {
            const createdTopic = await blogCommandCenterService.createTopic({
                title: newTopic.title.trim(),
                description: newTopic.description.trim() || null,
                seo_keywords: newTopic.seo_keywords.split(',').map(k => k.trim()).filter(Boolean),
                target_word_count: newTopic.target_word_count,
                priority: newTopic.priority,
                generation_prompt: newTopic.generation_prompt.trim() || null,
                is_active: true
            });

            // Refresh topics and select the new one
            if (onTopicsChange) {
                await onTopicsChange();
            }
            setSelectedTopic(createdTopic);
            setShowNewTopicModal(false);
            setNewTopic({
                title: '',
                description: '',
                seo_keywords: '',
                target_word_count: 1000,
                priority: 50,
                generation_prompt: ''
            });
        } catch (error) {
            console.error('Error creating topic:', error);
            alert('Failed to create topic: ' + error.message);
        } finally {
            setCreatingTopic(false);
        }
    };

    const handleTopicChange = (e) => {
        const value = e.target.value;
        if (value === '__new__') {
            setShowNewTopicModal(true);
        } else {
            setSelectedTopic(topics?.find(t => t.id === parseInt(value)));
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Generation Form */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <SparklesIcon className="w-5 h-5 text-amber-400" />
                    Generate New Post
                </h3>

                <div className="space-y-4">
                    {/* Topic Selection */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                            Select Topic *
                        </label>
                        <select
                            value={selectedTopic?.id || ''}
                            onChange={handleTopicChange}
                            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="">Choose a topic...</option>
                            <option value="__new__" className="text-blue-400 font-medium">
                                ➕ Create New Topic...
                            </option>
                            {topics?.map(topic => (
                                <option key={topic.id} value={topic.id}>
                                    {topic.title} ({topic.post_count || 0} posts)
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Content Brief */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                            Content Brief (Optional)
                        </label>
                        <textarea
                            value={contentBrief}
                            onChange={(e) => setContentBrief(e.target.value)}
                            placeholder="Specific points to cover, angle, audience..."
                            rows={3}
                            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                    </div>

                    {/* Target Keywords */}
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">
                            Target Keywords (comma separated)
                        </label>
                        <input
                            type="text"
                            value={targetKeywords}
                            onChange={(e) => setTargetKeywords(e.target.value)}
                            placeholder="keyword1, keyword2, keyword3"
                            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>

                    {/* Options Grid */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                Content Length
                            </label>
                            <select
                                value={contentLength}
                                onChange={(e) => setContentLength(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="short">Short (~500 words)</option>
                                <option value="medium">Medium (~1000 words)</option>
                                <option value="long">Long (~1500 words)</option>
                                <option value="comprehensive">Comprehensive (~2500+ words)</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-slate-300 mb-2">
                                Tone
                            </label>
                            <select
                                value={tone}
                                onChange={(e) => setTone(e.target.value)}
                                className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            >
                                <option value="professional">Professional</option>
                                <option value="friendly">Friendly</option>
                                <option value="casual">Casual</option>
                                <option value="authoritative">Authoritative</option>
                                <option value="educational">Educational</option>
                            </select>
                        </div>
                    </div>

                    {/* Toggles */}
                    <div className="flex flex-wrap gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeFaqs}
                                onChange={(e) => setIncludeFaqs(e.target.checked)}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-900/50 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-300">Include FAQs</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={includeImages}
                                onChange={(e) => setIncludeImages(e.target.checked)}
                                className="w-4 h-4 rounded border-slate-600 bg-slate-900/50 text-blue-500 focus:ring-blue-500"
                            />
                            <span className="text-sm text-slate-300">Auto-select Images</span>
                        </label>
                    </div>

                    {/* Generate Button */}
                    <button
                        onClick={handleGenerate}
                        disabled={isGenerating || !selectedTopic}
                        className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all"
                    >
                        {isGenerating ? (
                            <>
                                <ArrowPathIcon className="w-5 h-5 animate-spin" />
                                Generating...
                            </>
                        ) : (
                            <>
                                <SparklesIcon className="w-5 h-5" />
                                Generate Post
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Generation History */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                        <ClockIcon className="w-5 h-5 text-blue-400" />
                        Recent Generations
                    </h3>
                    <button
                        onClick={loadHistory}
                        className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                    >
                        <ArrowPathIcon className={`w-4 h-4 text-slate-400 ${historyLoading ? 'animate-spin' : ''}`} />
                    </button>
                </div>

                <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {generationHistory.length === 0 ? (
                        <p className="text-slate-500 text-center py-8">No generations yet</p>
                    ) : (
                        generationHistory.map((gen) => (
                            <div
                                key={gen.id}
                                className="p-4 bg-slate-900/50 border border-slate-700/50 rounded-lg"
                            >
                                <div className="flex items-start justify-between">
                                    <div>
                                        <p className="text-white font-medium">
                                            {gen.topic?.title || 'Unknown Topic'}
                                        </p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            {formatDate(gen.created_at)} {formatTime(gen.created_at)}
                                        </p>
                                    </div>
                                    <span className={`px-2 py-1 rounded text-xs font-medium ${gen.status === 'completed' ? 'bg-emerald-500/20 text-emerald-400' :
                                        gen.status === 'generating' ? 'bg-blue-500/20 text-blue-400' :
                                            gen.status === 'failed' ? 'bg-red-500/20 text-red-400' :
                                                'bg-slate-500/20 text-slate-400'
                                        }`}>
                                        {gen.status}
                                    </span>
                                </div>
                                {gen.post && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <a
                                            href={`/admin/blog/edit/${gen.post.id}`}
                                            className="text-xs text-blue-400 hover:underline flex items-center gap-1"
                                        >
                                            <PencilSquareIcon className="w-3 h-3" />
                                            Edit Post
                                        </a>
                                        <a
                                            href={`/blog/${gen.post.slug}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-slate-400 hover:underline flex items-center gap-1"
                                        >
                                            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                                            Preview
                                        </a>
                                    </div>
                                )}
                                {gen.error_message && (
                                    <p className="text-xs text-red-400 mt-2">{gen.error_message}</p>
                                )}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* New Topic Modal */}
            {showNewTopicModal && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-200"
                    onClick={() => setShowNewTopicModal(false)}
                >
                    <div
                        className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto transform transition-all duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                                <PlusIcon className="w-5 h-5 text-blue-400" />
                                Create New Topic
                            </h3>
                            <button
                                onClick={() => setShowNewTopicModal(false)}
                                className="p-2 hover:bg-slate-700 rounded-lg"
                            >
                                <XMarkIcon className="w-4 h-4 text-slate-400" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            {/* Topic Title */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    Topic Title *
                                </label>
                                <input
                                    type="text"
                                    value={newTopic.title}
                                    onChange={(e) => setNewTopic(t => ({ ...t, title: e.target.value }))}
                                    placeholder="e.g., Study in Germany, DAAD Scholarship..."
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    Description
                                </label>
                                <textarea
                                    value={newTopic.description}
                                    onChange={(e) => setNewTopic(t => ({ ...t, description: e.target.value }))}
                                    placeholder="Brief description of this topic..."
                                    rows={2}
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                            </div>

                            {/* SEO Keywords */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    SEO Keywords (comma separated)
                                </label>
                                <input
                                    type="text"
                                    value={newTopic.seo_keywords}
                                    onChange={(e) => setNewTopic(t => ({ ...t, seo_keywords: e.target.value }))}
                                    placeholder="keyword1, keyword2, keyword3..."
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500"
                                />
                            </div>

                            {/* Generation Prompt */}
                            <div>
                                <label className="block text-sm font-medium text-slate-300 mb-1">
                                    AI Generation Prompt (Optional)
                                </label>
                                <textarea
                                    value={newTopic.generation_prompt}
                                    onChange={(e) => setNewTopic(t => ({ ...t, generation_prompt: e.target.value }))}
                                    placeholder="Custom instructions for AI when generating posts for this topic..."
                                    rows={3}
                                    className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 resize-none"
                                />
                                <p className="text-xs text-slate-500 mt-1">
                                    This prompt will be used as additional context when generating blog posts for this topic.
                                </p>
                            </div>

                            {/* Options Grid */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">
                                        Target Word Count
                                    </label>
                                    <input
                                        type="number"
                                        min="300"
                                        max="5000"
                                        step="100"
                                        value={newTopic.target_word_count}
                                        onChange={(e) => setNewTopic(t => ({ ...t, target_word_count: parseInt(e.target.value) || 1000 }))}
                                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-slate-300 mb-1">
                                        Priority (0-100)
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        value={newTopic.priority}
                                        onChange={(e) => setNewTopic(t => ({ ...t, priority: parseInt(e.target.value) || 0 }))}
                                        className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                                    />
                                    <p className="text-xs text-slate-500 mt-1">
                                        Higher priority topics are selected first for auto-generation.
                                    </p>
                                </div>
                            </div>

                            {/* Create Button */}
                            <button
                                onClick={handleCreateTopic}
                                disabled={creatingTopic || !newTopic.title.trim()}
                                className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-all"
                            >
                                {creatingTopic ? (
                                    <>
                                        <ArrowPathIcon className="w-5 h-5 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <PlusIcon className="w-5 h-5" />
                                        Create Topic & Select
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// POSTS TAB
// =============================================================================

function PostsTab() {
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [statusFilter, setStatusFilter] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [viewMode, setViewMode] = useState('list');

    useEffect(() => {
        loadPosts();
    }, [page, statusFilter, searchQuery]);

    const loadPosts = async () => {
        setLoading(true);
        try {
            const data = await blogCommandCenterService.getPosts({
                page,
                limit: 20,
                status: statusFilter,
                search: searchQuery || null
            });
            setPosts(data.items || []);
            setTotalPages(data.totalPages || 1);
        } catch (error) {
            console.error('Error loading posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async (postId) => {
        try {
            await blogCommandCenterService.publishPost(postId);
            loadPosts();
        } catch (error) {
            console.error('Error publishing post:', error);
        }
    };

    const handleUnpublish = async (postId) => {
        try {
            await blogCommandCenterService.unpublishPost(postId);
            loadPosts();
        } catch (error) {
            console.error('Error unpublishing post:', error);
        }
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('Are you sure you want to delete this post?')) return;
        try {
            await blogCommandCenterService.deletePost(postId);
            loadPosts();
        } catch (error) {
            console.error('Error deleting post:', error);
        }
    };

    return (
        <div className="space-y-4">
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[200px]">
                    <div className="relative">
                        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            placeholder="Search posts..."
                            className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white placeholder-slate-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                    </div>
                </div>
                <select
                    value={statusFilter || ''}
                    onChange={(e) => setStatusFilter(e.target.value || null)}
                    className="px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                >
                    <option value="">All Status</option>
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="in_review">In Review</option>
                </select>
                <div className="flex items-center bg-slate-800/50 border border-slate-700/50 rounded-lg">
                    <button
                        onClick={() => setViewMode('list')}
                        className={`p-2 ${viewMode === 'list' ? 'bg-slate-700' : ''} rounded-l-lg`}
                    >
                        <ListBulletIcon className="w-4 h-4 text-slate-400" />
                    </button>
                    <button
                        onClick={() => setViewMode('grid')}
                        className={`p-2 ${viewMode === 'grid' ? 'bg-slate-700' : ''} rounded-r-lg`}
                    >
                        <GridIcon className="w-4 h-4 text-slate-400" />
                    </button>
                </div>
            </div>

            {/* Posts List */}
            {loading ? (
                <div className="flex items-center justify-center h-64">
                    <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-400" />
                </div>
            ) : posts.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                    No posts found
                </div>
            ) : viewMode === 'list' ? (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                    <table className="w-full">
                        <thead className="bg-slate-900/50">
                            <tr>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Title</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Topic</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Status</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">SEO</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Views</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Date</th>
                                <th className="text-right px-4 py-3 text-sm font-medium text-slate-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {posts.map((post) => (
                                <tr key={post.id} className="hover:bg-slate-700/30 transition-colors">
                                    <td className="px-4 py-3">
                                        <p className="text-white font-medium truncate max-w-[300px]">{post.title}</p>
                                        <p className="text-xs text-slate-500 truncate max-w-[300px]">/{post.slug}</p>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-slate-400">{post.topic?.title || 'N/A'}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`px-2 py-1 rounded text-xs font-medium border ${STATUS_COLORS[post.status || 'draft']}`}>
                                            {post.status || (post.is_published ? 'published' : 'draft')}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {post.seo_score != null ? (
                                            <span className={`text-sm font-medium ${getSeoScoreLabel(post.seo_score).color}`}>
                                                {post.seo_score}/100
                                            </span>
                                        ) : (
                                            <span className="text-xs text-slate-500">Not audited</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-slate-400">{formatNumber(post.views || 0)}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className="text-sm text-slate-400">{formatDate(post.created_at)}</span>
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-end gap-2">
                                            <a
                                                href={`/admin/blog/edit/${post.id}`}
                                                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                                            >
                                                <PencilSquareIcon className="w-4 h-4 text-slate-400" />
                                            </a>
                                            {post.is_published ? (
                                                <button
                                                    onClick={() => handleUnpublish(post.id)}
                                                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                                                >
                                                    <XCircleIcon className="w-4 h-4 text-amber-400" />
                                                </button>
                                            ) : (
                                                <button
                                                    onClick={() => handlePublish(post.id)}
                                                    className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                                                >
                                                    <CheckCircleIcon className="w-4 h-4 text-emerald-400" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => handleDelete(post.id)}
                                                className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                                            >
                                                <TrashIcon className="w-4 h-4 text-red-400" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {posts.map((post) => (
                        <div key={post.id} className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4">
                            <div className="aspect-video bg-slate-900/50 rounded-lg mb-3 overflow-hidden">
                                {post.image_url ? (
                                    <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                                ) : (
                                    <div className="w-full h-full flex items-center justify-center">
                                        <PhotoIcon className="w-8 h-8 text-slate-600" />
                                    </div>
                                )}
                            </div>
                            <h4 className="text-white font-medium truncate">{post.title}</h4>
                            <div className="flex items-center gap-2 mt-2">
                                <span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLORS[post.status || 'draft']}`}>
                                    {post.status || 'draft'}
                                </span>
                                {post.seo_score != null && (
                                    <span className={`text-xs ${getSeoScoreLabel(post.seo_score).color}`}>
                                        SEO: {post.seo_score}
                                    </span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-center gap-2">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-400 disabled:opacity-50"
                    >
                        Previous
                    </button>
                    <span className="text-slate-400">Page {page} of {totalPages}</span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-slate-400 disabled:opacity-50"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// SCHEDULE TAB
// =============================================================================

function ScheduleTab() {
    const [schedule, setSchedule] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        loadSchedule();
    }, []);

    const loadSchedule = async () => {
        setLoading(true);
        try {
            const data = await blogCommandCenterService.getScheduleConfig();
            setSchedule(data || {
                is_enabled: false,
                frequency: 'daily',
                time_of_day: '09:00',
                posts_per_cycle: 1,
                topic_rotation_strategy: 'round_robin',
                auto_publish: false,
                require_review: true
            });
        } catch (error) {
            console.error('Error loading schedule:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggle = async () => {
        try {
            await blogCommandCenterService.toggleSchedule(!schedule.is_enabled);
            await loadSchedule();
        } catch (error) {
            console.error('Error toggling schedule:', error);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await blogCommandCenterService.updateScheduleConfig(schedule);
            alert('Schedule saved successfully');
        } catch (error) {
            console.error('Error saving schedule:', error);
            alert('Failed to save schedule');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-400" />
            </div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            {/* Schedule Header */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <div className="flex items-center justify-between mb-6">
                    <div>
                        <h3 className="text-lg font-semibold text-white">Auto-Generation Schedule</h3>
                        <p className="text-sm text-slate-400 mt-1">
                            Automatically generate and schedule blog posts
                        </p>
                    </div>
                    <button
                        onClick={handleToggle}
                        className={`relative w-14 h-7 rounded-full transition-colors ${schedule.is_enabled ? 'bg-blue-600' : 'bg-slate-600'
                            }`}
                    >
                        <span
                            className={`absolute top-1 w-5 h-5 rounded-full bg-white transition-all ${schedule.is_enabled ? 'left-8' : 'left-1'
                                }`}
                        />
                    </button>
                </div>

                {schedule.is_enabled && schedule.next_run_at && (
                    <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="flex items-center gap-2">
                            <ClockIcon className="w-4 h-4 text-blue-400" />
                            <span className="text-sm text-blue-400">
                                Next generation: {formatDate(schedule.next_run_at)} at {formatTime(schedule.next_run_at)}
                            </span>
                        </div>
                    </div>
                )}
            </div>

            {/* Schedule Configuration */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6 space-y-6">
                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Frequency</label>
                        <select
                            value={schedule.frequency}
                            onChange={(e) => setSchedule(s => ({ ...s, frequency: e.target.value }))}
                            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white"
                        >
                            <option value="hourly">Hourly</option>
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Time of Day</label>
                        <input
                            type="time"
                            value={schedule.time_of_day}
                            onChange={(e) => setSchedule(s => ({ ...s, time_of_day: e.target.value }))}
                            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white"
                        />
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Posts per Cycle</label>
                        <input
                            type="number"
                            min="1"
                            max="10"
                            value={schedule.posts_per_cycle}
                            onChange={(e) => setSchedule(s => ({ ...s, posts_per_cycle: parseInt(e.target.value) }))}
                            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-slate-300 mb-2">Topic Selection</label>
                        <select
                            value={schedule.topic_rotation_strategy}
                            onChange={(e) => setSchedule(s => ({ ...s, topic_rotation_strategy: e.target.value }))}
                            className="w-full px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white"
                        >
                            <option value="round_robin">Round Robin</option>
                            <option value="random">Random</option>
                            <option value="priority">By Priority</option>
                            <option value="balanced">Balanced (fewer posts first)</option>
                        </select>
                    </div>
                </div>

                <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={schedule.require_review}
                            onChange={(e) => setSchedule(s => ({ ...s, require_review: e.target.checked, auto_publish: e.target.checked ? false : s.auto_publish }))}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-900/50 text-blue-500"
                        />
                        <span className="text-sm text-slate-300">Require manual review before publishing</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input
                            type="checkbox"
                            checked={schedule.auto_publish}
                            onChange={(e) => setSchedule(s => ({ ...s, auto_publish: e.target.checked, require_review: e.target.checked ? false : s.require_review }))}
                            className="w-4 h-4 rounded border-slate-600 bg-slate-900/50 text-blue-500"
                        />
                        <span className="text-sm text-slate-300">Auto-publish immediately</span>
                    </label>
                </div>

                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white font-medium flex items-center justify-center gap-2 transition-colors"
                >
                    {saving ? (
                        <>
                            <ArrowPathIcon className="w-4 h-4 animate-spin" />
                            Saving...
                        </>
                    ) : (
                        <>
                            <BookmarkIcon className="w-4 h-4" />
                            Save Schedule
                        </>
                    )}
                </button>
            </div>
        </div>
    );
}

// =============================================================================
// SEO AUDIT TAB
// =============================================================================

function SeoAuditTab() {
    const [postsNeedingAudit, setPostsNeedingAudit] = useState([]);
    const [selectedPost, setSelectedPost] = useState(null);
    const [audit, setAudit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [auditing, setAuditing] = useState(false);

    useEffect(() => {
        loadPostsNeedingAudit();
    }, []);

    const loadPostsNeedingAudit = async () => {
        setLoading(true);
        try {
            const data = await blogCommandCenterService.getPostsNeedingAudit({ limit: 20 });
            setPostsNeedingAudit(data || []);
        } catch (error) {
            console.error('Error loading posts:', error);
        } finally {
            setLoading(false);
        }
    };

    const runAudit = async (postId) => {
        setAuditing(true);
        setSelectedPost(postId);
        try {
            const result = await blogCommandCenterService.runSeoAudit(postId);
            setAudit(result);
            await loadPostsNeedingAudit();
        } catch (error) {
            console.error('Error running audit:', error);
            alert('Failed to run SEO audit');
        } finally {
            setAuditing(false);
        }
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Posts Needing Audit */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <ExclamationTriangleIcon className="w-5 h-5 text-amber-400" />
                    Posts Needing SEO Audit
                </h3>

                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-400" />
                    </div>
                ) : postsNeedingAudit.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                        <CheckCircleIcon className="w-12 h-12 mx-auto mb-2 text-emerald-400/50" />
                        All posts are audited!
                    </div>
                ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                        {postsNeedingAudit.map((post) => (
                            <div
                                key={post.id}
                                className="p-4 bg-slate-900/50 border border-slate-700/50 rounded-lg"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-white font-medium truncate">{post.title}</p>
                                        <p className="text-xs text-slate-500 mt-1">
                                            Last audited: {post.last_seo_audit_at ? formatDate(post.last_seo_audit_at) : 'Never'}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => runAudit(post.id)}
                                        disabled={auditing && selectedPost === post.id}
                                        className="ml-3 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-sm text-white flex items-center gap-1"
                                    >
                                        {auditing && selectedPost === post.id ? (
                                            <ArrowPathIcon className="w-3 h-3 animate-spin" />
                                        ) : (
                                            <FlagIcon className="w-3 h-3" />
                                        )}
                                        Audit
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Audit Results */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <TrophyIcon className="w-5 h-5 text-purple-400" />
                    Audit Results
                </h3>

                {!audit ? (
                    <div className="text-center py-12 text-slate-500">
                        <FlagIcon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                        Select a post to run SEO audit
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* Overall Score */}
                        <div className="text-center py-4">
                            <div className={`text-5xl font-bold ${getSeoScoreLabel(audit.overall_score).color}`}>
                                {audit.overall_score}
                            </div>
                            <p className={`text-sm mt-1 ${getSeoScoreLabel(audit.overall_score).color}`}>
                                {getSeoScoreLabel(audit.overall_score).label}
                            </p>
                        </div>

                        {/* Score Breakdown */}
                        <div className="space-y-2">
                            {[
                                { label: 'Title', score: audit.title_score },
                                { label: 'Meta Description', score: audit.meta_description_score },
                                { label: 'Content Length', score: audit.content_length_score },
                                { label: 'Readability', score: audit.readability_score },
                                { label: 'Keywords', score: audit.keyword_density_score },
                                { label: 'Headings', score: audit.heading_structure_score },
                                { label: 'Internal Links', score: audit.internal_links_score },
                                { label: 'Images', score: audit.image_alt_score }
                            ].map((item) => (
                                <div key={item.label} className="flex items-center gap-3">
                                    <span className="text-sm text-slate-400 w-32">{item.label}</span>
                                    <div className="flex-1 h-2 bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full ${item.score >= 70 ? 'bg-emerald-500' :
                                                item.score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                                                }`}
                                            style={{ width: `${item.score}%` }}
                                        />
                                    </div>
                                    <span className={`text-sm w-10 text-right ${getSeoScoreLabel(item.score).color}`}>
                                        {item.score}
                                    </span>
                                </div>
                            ))}
                        </div>

                        {/* Issues */}
                        {audit.issues && audit.issues.length > 0 && (
                            <div className="mt-4">
                                <h4 className="text-sm font-medium text-slate-300 mb-2">Issues Found</h4>
                                <div className="space-y-2">
                                    {audit.issues.map((issue, i) => (
                                        <div
                                            key={i}
                                            className={`p-3 rounded-lg border ${issue.severity === 'high' ? 'bg-red-500/10 border-red-500/30' :
                                                issue.severity === 'medium' ? 'bg-amber-500/10 border-amber-500/30' :
                                                    'bg-slate-500/10 border-slate-500/30'
                                                }`}
                                        >
                                            <p className={`text-sm ${issue.severity === 'high' ? 'text-red-400' :
                                                issue.severity === 'medium' ? 'text-amber-400' : 'text-slate-400'
                                                }`}>
                                                {issue.message}
                                            </p>
                                            {issue.suggestion && (
                                                <p className="text-xs text-slate-500 mt-1">{issue.suggestion}</p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// KEYWORDS TAB
// =============================================================================

function KeywordsTab() {
    const [keywords, setKeywords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [newKeyword, setNewKeyword] = useState('');
    const [adding, setAdding] = useState(false);

    useEffect(() => {
        loadKeywords();
    }, []);

    const loadKeywords = async () => {
        setLoading(true);
        try {
            const data = await blogCommandCenterService.getKeywords({ limit: 50 });
            setKeywords(data.items || []);
        } catch (error) {
            console.error('Error loading keywords:', error);
        } finally {
            setLoading(false);
        }
    };

    const addKeyword = async () => {
        if (!newKeyword.trim()) return;
        setAdding(true);
        try {
            await blogCommandCenterService.upsertKeyword({ keyword: newKeyword.trim() });
            setNewKeyword('');
            await loadKeywords();
        } catch (error) {
            console.error('Error adding keyword:', error);
        } finally {
            setAdding(false);
        }
    };

    const deleteKeyword = async (id) => {
        try {
            await blogCommandCenterService.deleteKeyword(id);
            await loadKeywords();
        } catch (error) {
            console.error('Error deleting keyword:', error);
        }
    };

    return (
        <div className="space-y-6">
            {/* Add Keyword */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <PlusIcon className="w-5 h-5 text-blue-400" />
                    Track New Keyword
                </h3>
                <div className="flex gap-3">
                    <input
                        type="text"
                        value={newKeyword}
                        onChange={(e) => setNewKeyword(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && addKeyword()}
                        placeholder="Enter keyword to track..."
                        className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                    />
                    <button
                        onClick={addKeyword}
                        disabled={adding || !newKeyword.trim()}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white font-medium flex items-center gap-2"
                    >
                        {adding ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <PlusIcon className="w-4 h-4" />}
                        Add
                    </button>
                </div>
            </div>

            {/* Keywords Table */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl overflow-hidden">
                {loading ? (
                    <div className="flex items-center justify-center h-48">
                        <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-400" />
                    </div>
                ) : keywords.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                        No keywords tracked yet
                    </div>
                ) : (
                    <table className="w-full">
                        <thead className="bg-slate-900/50">
                            <tr>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Keyword</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Volume</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Difficulty</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Trend</th>
                                <th className="text-left px-4 py-3 text-sm font-medium text-slate-400">Used In</th>
                                <th className="text-right px-4 py-3 text-sm font-medium text-slate-400">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700/50">
                            {keywords.map((kw) => (
                                <tr key={kw.id} className="hover:bg-slate-700/30 transition-colors">
                                    <td className="px-4 py-3 text-white font-medium">{kw.keyword}</td>
                                    <td className="px-4 py-3 text-slate-400">
                                        {kw.search_volume ? formatNumber(kw.search_volume) : '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        {kw.difficulty != null ? (
                                            <span className={`${kw.difficulty <= 30 ? 'text-emerald-400' :
                                                kw.difficulty <= 60 ? 'text-amber-400' : 'text-red-400'
                                                }`}>
                                                {kw.difficulty}
                                            </span>
                                        ) : '-'}
                                    </td>
                                    <td className="px-4 py-3">
                                        <span className={`text-sm ${kw.trend === 'up' ? 'text-emerald-400' :
                                            kw.trend === 'down' ? 'text-red-400' : 'text-slate-400'
                                            }`}>
                                            {kw.trend === 'up' ? '↑ Rising' :
                                                kw.trend === 'down' ? '↓ Falling' : '-'}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-slate-400">
                                        {kw.used_in_posts?.length || 0} posts
                                    </td>
                                    <td className="px-4 py-3">
                                        <button
                                            onClick={() => deleteKeyword(kw.id)}
                                            className="p-2 hover:bg-slate-700 rounded-lg transition-colors float-right"
                                        >
                                            <TrashIcon className="w-4 h-4 text-red-400" />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// CONTENT QUEUE TAB
// =============================================================================

function ContentQueueTab() {
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newItem, setNewItem] = useState({
        title: '',
        description: '',
        target_keywords: '',
        priority: 50
    });

    useEffect(() => {
        loadQueue();
    }, []);

    const loadQueue = async () => {
        setLoading(true);
        try {
            const data = await blogCommandCenterService.getContentQueue({
                status: ['idea', 'researching', 'queued']
            });
            setQueue(data.items || []);
        } catch (error) {
            console.error('Error loading queue:', error);
        } finally {
            setLoading(false);
        }
    };

    const addToQueue = async () => {
        try {
            await blogCommandCenterService.addToContentQueue({
                ...newItem,
                target_keywords: newItem.target_keywords.split(',').map(k => k.trim()).filter(Boolean)
            });
            setShowAddModal(false);
            setNewItem({ title: '', description: '', target_keywords: '', priority: 50 });
            await loadQueue();
        } catch (error) {
            console.error('Error adding to queue:', error);
        }
    };

    const deleteItem = async (id) => {
        try {
            await blogCommandCenterService.deleteContentQueueItem(id);
            await loadQueue();
        } catch (error) {
            console.error('Error deleting item:', error);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    <Squares2X2Icon className="w-5 h-5 text-purple-400" />
                    Content Ideas Queue
                </h3>
                <button
                    onClick={() => setShowAddModal(true)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-white font-medium flex items-center gap-2"
                >
                    <PlusIcon className="w-4 h-4" />
                    Add Idea
                </button>
            </div>

            {/* Queue List */}
            {loading ? (
                <div className="flex items-center justify-center h-48">
                    <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-400" />
                </div>
            ) : queue.length === 0 ? (
                <div className="text-center py-12 bg-slate-800/50 border border-slate-700/50 rounded-xl text-slate-500">
                    <Squares2X2Icon className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    No content ideas in queue
                </div>
            ) : (
                <div className="space-y-3">
                    {queue.map((item) => (
                        <div
                            key={item.id}
                            className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4"
                        >
                            <div className="flex items-start justify-between">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <h4 className="text-white font-medium">{item.title}</h4>
                                        <span className={`px-2 py-0.5 rounded text-xs ${item.status === 'idea' ? 'bg-slate-500/20 text-slate-400' :
                                            item.status === 'researching' ? 'bg-blue-500/20 text-blue-400' :
                                                'bg-purple-500/20 text-purple-400'
                                            }`}>
                                            {item.status}
                                        </span>
                                        <span className="text-xs text-slate-500">
                                            Priority: {item.priority}
                                        </span>
                                    </div>
                                    {item.description && (
                                        <p className="text-sm text-slate-400 mt-1">{item.description}</p>
                                    )}
                                    {item.target_keywords?.length > 0 && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                            {item.target_keywords.map((kw, i) => (
                                                <span key={i} className="px-2 py-0.5 bg-slate-700/50 rounded text-xs text-slate-400">
                                                    {kw}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center gap-2 ml-4">
                                    <button
                                        onClick={() => deleteItem(item.id)}
                                        className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
                                    >
                                        <TrashIcon className="w-4 h-4 text-red-400" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Add Modal */}
            {showAddModal && (
                <div
                    className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 transition-opacity duration-200"
                    onClick={() => setShowAddModal(false)}
                >
                    <div
                        className="bg-slate-800 border border-slate-700 rounded-xl p-6 w-full max-w-lg transform transition-all duration-200"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white">Add Content Idea</h3>
                            <button onClick={() => setShowAddModal(false)} className="p-2 hover:bg-slate-700 rounded-lg">
                                <XMarkIcon className="w-4 h-4 text-slate-400" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-slate-300 mb-1">Title *</label>
                                <input
                                    type="text"
                                    value={newItem.title}
                                    onChange={(e) => setNewItem(n => ({ ...n, title: e.target.value }))}
                                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white"
                                    placeholder="Post title or idea..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-300 mb-1">Description</label>
                                <textarea
                                    value={newItem.description}
                                    onChange={(e) => setNewItem(n => ({ ...n, description: e.target.value }))}
                                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white resize-none"
                                    rows={3}
                                    placeholder="Brief description or notes..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-300 mb-1">Target Keywords (comma separated)</label>
                                <input
                                    type="text"
                                    value={newItem.target_keywords}
                                    onChange={(e) => setNewItem(n => ({ ...n, target_keywords: e.target.value }))}
                                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white"
                                    placeholder="keyword1, keyword2..."
                                />
                            </div>
                            <div>
                                <label className="block text-sm text-slate-300 mb-1">Priority (0-100)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={newItem.priority}
                                    onChange={(e) => setNewItem(n => ({ ...n, priority: parseInt(e.target.value) || 0 }))}
                                    className="w-full px-4 py-2 bg-slate-900/50 border border-slate-600 rounded-lg text-white"
                                />
                            </div>
                            <button
                                onClick={addToQueue}
                                disabled={!newItem.title.trim()}
                                className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white font-medium"
                            >
                                Add to Queue
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =============================================================================
// ANALYTICS TAB
// =============================================================================

function AnalyticsTab() {
    const [analytics, setAnalytics] = useState(null);
    const [topPosts, setTopPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState(30);

    useEffect(() => {
        loadAnalytics();
    }, [period]);

    const loadAnalytics = async () => {
        setLoading(true);
        try {
            const [analyticsData, topPostsData] = await Promise.all([
                blogCommandCenterService.getAnalyticsOverview({ days: period }),
                blogCommandCenterService.getTopPosts({ limit: 10, days: period })
            ]);
            setAnalytics(analyticsData);
            setTopPosts(topPostsData || []);
        } catch (error) {
            console.error('Error loading analytics:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <ArrowPathIcon className="w-8 h-8 animate-spin text-blue-400" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">Analytics Overview</h3>
                <select
                    value={period}
                    onChange={(e) => setPeriod(parseInt(e.target.value))}
                    className="px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-white"
                >
                    <option value={7}>Last 7 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={90}>Last 90 days</option>
                </select>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard
                    title="Total Views"
                    value={formatNumber(analytics?.totalViews || 0)}
                    icon={EyeIcon}
                    color="blue"
                />
                <StatCard
                    title="Unique Visitors"
                    value={formatNumber(analytics?.totalVisitors || 0)}
                    icon={ArrowTrendingUpIcon}
                    color="emerald"
                />
                <StatCard
                    title="Avg Daily Views"
                    value={formatNumber(analytics?.avgDailyViews || 0)}
                    icon={ChartBarIcon}
                    color="purple"
                />
                <StatCard
                    title="Period"
                    value={`${period} days`}
                    icon={CalendarIcon}
                    color="cyan"
                />
            </div>

            {/* Top Posts */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <TrophyIcon className="w-5 h-5 text-amber-400" />
                    Top Performing Posts
                </h3>
                {topPosts.length === 0 ? (
                    <p className="text-slate-500 text-center py-8">No data available</p>
                ) : (
                    <div className="space-y-3">
                        {topPosts.map((post, index) => (
                            <div
                                key={post.id}
                                className="flex items-center gap-4 p-3 bg-slate-900/50 rounded-lg"
                            >
                                <span className={`text-lg font-bold ${index === 0 ? 'text-amber-400' :
                                    index === 1 ? 'text-slate-300' :
                                        index === 2 ? 'text-amber-600' : 'text-slate-500'
                                    }`}>
                                    #{index + 1}
                                </span>
                                <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium truncate">{post.title}</p>
                                    <p className="text-xs text-slate-500">{post.topic?.title || 'Uncategorized'}</p>
                                </div>
                                <div className="text-right">
                                    <p className="text-white font-medium">{formatNumber(post.views || 0)}</p>
                                    <p className="text-xs text-slate-500">views</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

// =============================================================================
// IMAGES TAB (Unsplash)
// =============================================================================

function ImagesTab() {
    const [query, setQuery] = useState('');
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searched, setSearched] = useState(false);

    const searchImages = async () => {
        if (!query.trim()) return;
        setLoading(true);
        setSearched(true);
        try {
            const data = await blogCommandCenterService.searchUnsplashImages(query);
            setImages(data.results || []);
        } catch (error) {
            console.error('Error searching images:', error);
            alert('Failed to search images. Make sure Unsplash API key is configured.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Search */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <PhotoIcon className="w-5 h-5 text-purple-400" />
                    Search Unsplash Images
                </h3>
                <div className="flex gap-3">
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && searchImages()}
                        placeholder="Search for images..."
                        className="flex-1 px-4 py-3 bg-slate-900/50 border border-slate-600 rounded-lg text-white placeholder-slate-500"
                    />
                    <button
                        onClick={searchImages}
                        disabled={loading || !query.trim()}
                        className="px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg text-white font-medium flex items-center gap-2"
                    >
                        {loading ? <ArrowPathIcon className="w-4 h-4 animate-spin" /> : <MagnifyingGlassIcon className="w-4 h-4" />}
                        Search
                    </button>
                </div>
            </div>

            {/* Results */}
            {searched && (
                <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                    {loading ? (
                        <div className="flex items-center justify-center h-48">
                            <ArrowPathIcon className="w-6 h-6 animate-spin text-blue-400" />
                        </div>
                    ) : images.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            No images found for "{query}"
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                            {images.map((image) => (
                                <div
                                    key={image.id}
                                    className="relative group aspect-video bg-slate-900/50 rounded-lg overflow-hidden cursor-pointer"
                                >
                                    <img
                                        src={image.urls.small}
                                        alt={image.alt_description || 'Unsplash image'}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end">
                                        <div className="p-3 w-full">
                                            <p className="text-xs text-white truncate">
                                                by {image.user.name}
                                            </p>
                                            <button className="mt-2 w-full py-1.5 bg-blue-600 hover:bg-blue-700 rounded text-xs text-white">
                                                Use Image
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

// =============================================================================
// SETTINGS TAB
// =============================================================================

function SettingsTab({ topics, onTopicUpdate }) {
    const [loading, setLoading] = useState(false);

    const handleToggleTopic = async (topicId, isActive) => {
        try {
            await blogCommandCenterService.updateTopic(topicId, { is_active: isActive });
            if (onTopicUpdate) onTopicUpdate();
        } catch (error) {
            console.error('Error updating topic:', error);
        }
    };

    return (
        <div className="max-w-3xl mx-auto space-y-6">
            {/* Topics Management */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Squares2X2Icon className="w-5 h-5 text-blue-400" />
                    Topic Management
                </h3>
                <div className="space-y-3">
                    {topics?.map((topic) => (
                        <div
                            key={topic.id}
                            className="flex items-center justify-between p-4 bg-slate-900/50 border border-slate-700/50 rounded-lg"
                        >
                            <div>
                                <p className="text-white font-medium">{topic.title}</p>
                                <p className="text-xs text-slate-500">
                                    {topic.post_count || 0} posts • Priority: {topic.priority || 0}
                                </p>
                            </div>
                            <button
                                onClick={() => handleToggleTopic(topic.id, !topic.is_active)}
                                className={`relative w-12 h-6 rounded-full transition-colors ${topic.is_active ? 'bg-blue-600' : 'bg-slate-600'
                                    }`}
                            >
                                <span
                                    className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-all ${topic.is_active ? 'left-7' : 'left-1'
                                        }`}
                                />
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {/* API Configuration Info */}
            <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                    <Cog6ToothIcon className="w-5 h-5 text-amber-400" />
                    API Configuration
                </h3>
                <div className="space-y-3">
                    <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                        <span className="text-slate-400">OpenRouter API</span>
                        <span className="text-emerald-400">Configured</span>
                    </div>
                    <div className="flex items-center justify-between py-2 border-b border-slate-700/50">
                        <span className="text-slate-400">Unsplash API</span>
                        <span className={import.meta.env.VITE_UNSPLASH_ACCESS_KEY ? 'text-emerald-400' : 'text-amber-400'}>
                            {import.meta.env.VITE_UNSPLASH_ACCESS_KEY ? 'Configured' : 'Not Configured'}
                        </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                        <span className="text-slate-400">Blog Writer Edge Function</span>
                        <span className="text-emerald-400">Active</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function BlogSEOCommandCenter() {
    const [activeTab, setActiveTab] = useState('overview');
    const [stats, setStats] = useState(null);
    const [topics, setTopics] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadInitialData();
    }, []);

    const loadInitialData = async () => {
        setLoading(true);
        try {
            const [statsData, topicsData] = await Promise.all([
                blogCommandCenterService.getCommandCenterStats(),
                blogCommandCenterService.getTopicsWithStats()
            ]);
            setStats(statsData);
            setTopics(topicsData || []);
        } catch (error) {
            console.error('Error loading initial data:', error);
        } finally {
            setLoading(false);
        }
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return <OverviewTab stats={stats} loading={loading} onRefresh={loadInitialData} />;
            case 'generate':
                return <GenerateTab topics={topics} onGenerate={loadInitialData} onTopicsChange={loadInitialData} />;
            case 'posts':
                return <PostsTab />;
            case 'schedule':
                return <ScheduleTab />;
            case 'seo':
                return <SeoAuditTab />;
            case 'keywords':
                return <KeywordsTab />;
            case 'queue':
                return <ContentQueueTab />;
            case 'analytics':
                return <AnalyticsTab />;
            case 'images':
                return <ImagesTab />;
            case 'settings':
                return <SettingsTab topics={topics} onTopicUpdate={loadInitialData} />;
            default:
                return null;
        }
    };

    return (
        <div className="min-h-screen bg-slate-900 text-white">
            {/* Header */}
            <div className="bg-slate-800/50 border-b border-slate-700/50">
                <div className="max-w-7xl mx-auto px-6 py-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="p-3 bg-gradient-to-br from-blue-600 to-purple-600 rounded-xl">
                                <PencilIcon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-bold text-white">Blog SEO Command Center</h1>
                                <p className="text-sm text-slate-400">AI-powered blog generation, SEO, and analytics</p>
                            </div>
                        </div>
                        <button
                            onClick={loadInitialData}
                            className="p-2 hover:bg-slate-700/50 rounded-lg transition-colors"
                        >
                            <ArrowPathIcon className={`w-5 h-5 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
                        </button>
                    </div>
                </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-slate-800/30 border-b border-slate-700/50 overflow-x-auto">
                <div className="max-w-7xl mx-auto px-6">
                    <div className="flex space-x-1">
                        {TABS.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-all border-b-2 whitespace-nowrap ${activeTab === tab.id
                                    ? 'text-blue-400 border-blue-400'
                                    : 'text-slate-400 border-transparent hover:text-white hover:border-slate-600'
                                    }`}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Tab Content */}
            <div className="max-w-7xl mx-auto px-6 py-6">
                <div className="transition-all duration-200">
                    {renderTabContent()}
                </div>
            </div>
        </div>
    );
}